/**
 * Icônes InfraNet GN — traits 1.5, 20 px, currentColor.
 *
 * Remplace les emojis de navigation : un emoji se rend différemment sur chaque OS et ne
 * s'aligne jamais. Chaque icône est un tracé maison minimal ; `aria-hidden` par défaut
 * (l'intitulé du lien porte le sens), `decorative=false` pour une icône porteuse de sens.
 */
import type { SVGProps } from "react";

type Props = SVGProps<SVGSVGElement> & { size?: number };

function Base({ size = 20, children, ...props }: Props) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      {children}
    </svg>
  );
}

export const IconAccueil = (p: Props) => (
  <Base {...p}>
    <path d="M3 11 12 3l9 8" />
    <path d="M5 10v10h5v-6h4v6h5V10" />
  </Base>
);

export const IconCarte = (p: Props) => (
  <Base {...p}>
    <path d="M9 4 3 6v14l6-2 6 2 6-2V4l-6 2-6-2Z" />
    <path d="M9 4v14M15 6v14" />
  </Base>
);

export const IconTopologie = (p: Props) => (
  <Base {...p}>
    <rect x="9" y="3" width="6" height="6" rx="1" />
    <rect x="2" y="15" width="6" height="6" rx="1" />
    <rect x="16" y="15" width="6" height="6" rx="1" />
    <path d="M12 9v3M5 15v-3h14v3" />
  </Base>
);

export const IconServices = (p: Props) => (
  <Base {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18M12 3c3 3.5 3 14 0 18M12 3c-3 3.5-3 14 0 18" />
  </Base>
);

export const IconIncident = (p: Props) => (
  <Base {...p}>
    <path d="M12 3 2 20h20L12 3Z" />
    <path d="M12 10v4M12 17.5v.5" />
  </Base>
);

export const IconMission = (p: Props) => (
  <Base {...p}>
    <path d="M12 21s-7-5.5-7-11a7 7 0 0 1 14 0c0 5.5-7 11-7 11Z" />
    <circle cx="12" cy="10" r="2.5" />
  </Base>
);

export const IconIntelligence = (p: Props) => (
  <Base {...p}>
    <path d="M12 3a6 6 0 0 0-4 10.5c.8.7 1 1.5 1 2.5h6c0-1 .2-1.8 1-2.5A6 6 0 0 0 12 3Z" />
    <path d="M9.5 19h5M10.5 21.5h3" />
  </Base>
);

export const IconQualite = (p: Props) => (
  <Base {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="m8 12.5 2.5 2.5L16 9.5" />
  </Base>
);

export const IconDirection = (p: Props) => (
  <Base {...p}>
    <path d="M3 17h18M5 17V7l7 4 7-4v10" />
    <path d="M12 11V6" />
  </Base>
);

export const IconRapports = (p: Props) => (
  <Base {...p}>
    <path d="M6 3h9l4 4v14H6V3Z" />
    <path d="M15 3v4h4M9 12h6M9 16h6" />
  </Base>
);

export const IconCapacite = (p: Props) => (
  <Base {...p}>
    <path d="M4 20V10M10 20V4M16 20v-8M22 20H2" />
  </Base>
);

export const IconImport = (p: Props) => (
  <Base {...p}>
    <path d="M12 3v12M7 10l5 5 5-5" />
    <path d="M4 21h16" />
  </Base>
);

export const IconEditer = (p: Props) => (
  <Base {...p}>
    <path d="m14 5 5 5L8 21H3v-5L14 5Z" />
    <path d="m12 7 5 5" />
  </Base>
);

export const IconAdmin = (p: Props) => (
  <Base {...p}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19 12a7 7 0 0 0-.1-1.2l2-1.6-2-3.4-2.4 1a7 7 0 0 0-2-1.2L14 3h-4l-.5 2.6a7 7 0 0 0-2 1.2l-2.4-1-2 3.4 2 1.6a7 7 0 0 0 0 2.4l-2 1.6 2 3.4 2.4-1a7 7 0 0 0 2 1.2L10 21h4l.5-2.6a7 7 0 0 0 2-1.2l2.4 1 2-3.4-2-1.6c.06-.4.1-.8.1-1.2Z" />
  </Base>
);

export const IconAlerte = (p: Props) => (
  <Base {...p}>
    <path d="M12 3 2 20h20L12 3Z" />
    <path d="M12 10v4M12 17.5v.5" />
  </Base>
);

export const IconEnergie = (p: Props) => (
  <Base {...p}>
    <path d="M13 2 4 14h6l-1 8 9-12h-6l1-8Z" />
  </Base>
);

export const IconRecherche = (p: Props) => (
  <Base {...p}>
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-4-4" />
  </Base>
);

export const IconFermer = (p: Props) => (
  <Base {...p}>
    <path d="m5 5 14 14M19 5 5 19" />
  </Base>
);

export const IconMenu = (p: Props) => (
  <Base {...p}>
    <path d="M4 6h16M4 12h16M4 18h16" />
  </Base>
);

export const IconSite = (p: Props) => (
  <Base {...p}>
    <rect x="4" y="8" width="16" height="12" rx="1" />
    <path d="M9 20v-4h6v4M9 4h6v4H9z" />
  </Base>
);
